/**
 * Fee-runs client (Slice 5 cutover).
 *
 * Backend router: app/routers/agreement_fee_runs.py, prefix /agreement-fee-runs.
 * Firm-wide compute / preview / recompute + freeze, per-agreement frozen runs, and
 * the accountant export. The old agreement-fee-config client (fee config + what-if
 * preview + current-fee summary) was retired with the compute cutover; per-account
 * fee assignment now lives on the Billing-details rows (see api/accountBilling.ts).
 *
 * Money is integer euro-cents; rates are basis points.
 */
import { api } from './client';
import { downloadBlob } from './iaaExports';
import type { ISODate, ISODateTime, UUID } from './types';

/** A frozen per-period billing record (GET /agreement-fee-runs/{id}). */
export interface AgreementFeeRun {
  id: UUID;
  agreement_id: UUID;
  account_id: UUID;          // per-account grain (migration 0063, arc Inc 3)
  period_month: ISODate;
  aum_eur_cents: number | null;
  annual_fee_cents: number | null;
  net_fee_cents: number;
  vat_rate_bps: number;
  vat_cents: number;
  total_fee_cents: number;
  basis_snapshot: Record<string, unknown>;
  computed_at: ISODateTime;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  created_by: UUID | null;
  updated_by: UUID | null;
}

/** Result of a firm-wide batch compute+freeze (agreement labels per outcome bucket). */
export interface FeeRunBatchResult {
  period_month: ISODate;
  computed: string[];
  skipped_existing: string[];
  skipped_no_aum: string[];
  errors: string[];
  /** Assigned accounts all off-cycle this month (quarterly bills Mar/Jun/Sep/Dec,
   *  semiannual Jun/Dec) or all waived → no run produced. Not an error. */
  not_due?: string[];
  /** P35: assigned accounts with no fee-basis data yet (new, or first upload missing). */
  not_started?: string[];
}

/** One per-account row of the pre-freeze approval list (arc §5.5). */
export interface FeeRunPreviewRow {
  agreement_id: UUID;
  agreement_code: string | null;
  agreement_mnemonic: string | null;
  account_id: UUID;
  is_direct: boolean;
  insurer: string | null;
  policy_number: string | null;
  bank_name: string | null;
  account_number: string | null;
  billing_name: string | null;
  billing_complete: boolean;
  aum_eur_cents: number | null;
  effective_rate_bps: number | null;
  net_fee_cents: number;
  vat_cents: number;
  total_fee_cents: number;
}

/** Firm-wide pre-freeze preview: the per-account approval rows + skip buckets. */
export interface FeeRunBatchPreview {
  period_month: ISODate;
  rows: FeeRunPreviewRow[];
  skipped_existing: string[];
  skipped_no_aum: string[];
  errors: string[];
  /** Assigned accounts all off-cycle this month (quarterly Mar/Jun/Sep/Dec,
   *  semiannual Jun/Dec) or all waived → no approval rows. Not an error. */
  not_due?: string[];
  /** P35: assigned accounts with no fee-basis data yet (new, or first upload missing). */
  not_started?: string[];
  /** Recompute preview only: uploaded invoices that the recomputed fee would
   *  conflict with (> €1). The recompute is loudly gated on these. */
  invoice_conflicts: string[];
}

/** Pre-download gating check for the accountant export (arc Inc 4 / §3). */
export interface FeeExportReport {
  period_month: ISODate;
  total_rows: number;
  incomplete: string[];   // labels missing a billing field (name / TIN / address)
  drift: string[];        // labels whose billing differs from the snapshotted person
}

/** One WAIVED account-month (a billing exclusion, Pedro 2026-07-10 / Option B) with
 *  friendly labels — the Compute-Month "not billed" list. The computed fee is absent
 *  (deleted; never charged); this is the decision record. */
export interface Exclusion {
  id: UUID;
  agreement_id: UUID;
  account_id: UUID;
  period_month: ISODate;
  reason: string | null;
  created_at: ISODateTime;
  created_by: UUID | null;
  agreement_code: string | null;
  agreement_mnemonic: string | null;
  is_direct: boolean;
  insurer: string | null;
  policy_number: string | null;
  bank_name: string | null;
  account_number: string | null;
}

/** One active, fee-assigned account NOT due to bill this month because of its cadence
 *  (quarterly Mar/Jun/Sep/Dec, semiannual Jun/Dec) — the Compute-Month "not due this
 *  month" list. Field shape mirrors Exclusion so the Review surface reuses its labels. */
export interface NotDue {
  agreement_id: UUID;
  agreement_code: string | null;
  agreement_mnemonic: string | null;
  account_id: UUID;
  is_direct: boolean;
  bank_name: string | null;
  account_number: string | null;
  insurer: string | null;
  policy_number: string | null;
  fee_frequency: string;
  next_due_month: ISODate;
}

function _fileNameFromCD(cd: string, fallback: string): string {
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  if (star) return decodeURIComponent(star[1]);
  const plain = /filename="?([^";]+)"?/i.exec(cd);
  return plain ? plain[1] : fallback;
}
async function _detailOf(res: Response, fallback: string): Promise<string> {
  let detail: unknown = null;
  try { detail = (await res.json()).detail; } catch { /* non-JSON body */ }
  return typeof detail === 'string' ? detail : fallback;
}

export const feeRunsApi = {
  /** An agreement's frozen fee runs, oldest period first (per-account). */
  list: (agreementId: UUID): Promise<AgreementFeeRun[]> =>
    api.get<AgreementFeeRun[]>(`/agreement-fee-runs/${agreementId}`),

  /** Firm-wide PRE-FREEZE preview for one period (Admin) — computes, writes nothing. */
  previewBatch: (periodMonth: ISODate): Promise<FeeRunBatchPreview> =>
    api.post<FeeRunBatchPreview>('/agreement-fee-runs/compute-batch-preview', { period_month: periodMonth }),

  /** Firm-wide compute + FREEZE for one period (Admin) — the approve→freeze step. */
  computeBatch: (periodMonth: ISODate): Promise<FeeRunBatchResult> =>
    api.post<FeeRunBatchResult>('/agreement-fee-runs/compute-batch', { period_month: periodMonth }),

  /** Firm-wide RECOMPUTE preview (redo a frozen month after editing the 20th-AuM):
   *  computes the new values for ALL agreements + returns invoice_conflicts. */
  recomputeBatchPreview: (periodMonth: ISODate): Promise<FeeRunBatchPreview> =>
    api.post<FeeRunBatchPreview>('/agreement-fee-runs/recompute-batch-preview', { period_month: periodMonth }),

  /** Firm-wide RECOMPUTE + re-freeze: deletes the period's runs and recomputes from
   *  the current 20th-AuM (Admin; invoices kept). Review the preview's conflicts first. */
  recomputeBatch: (periodMonth: ISODate): Promise<FeeRunBatchResult> =>
    api.post<FeeRunBatchResult>('/agreement-fee-runs/recompute-batch', { period_month: periodMonth }),

  /** Delete one frozen fee run (Admin correction path). 204. */
  delete: (runId: UUID): Promise<void> =>
    api.delete<void>(`/agreement-fee-runs/${runId}`),

  /** Waive a computed fee — 'Excluded — not billed' (Admin, Option B). Deletes the run
   *  + records a keyed exclusion, so it can't leak into any total and compute/recompute
   *  skip it. Optional reason. 204. */
  exclude: (runId: UUID, reason?: string | null): Promise<void> =>
    api.post<void>(`/agreement-fee-runs/${runId}/exclude`, { reason: reason ?? null }),

  /** The month's waived account-months ('YYYY-MM'), with friendly labels (Admin). */
  listExclusions: (period: string): Promise<Exclusion[]> =>
    api.get<Exclusion[]>(`/agreement-fee-runs/exclusions?period=${encodeURIComponent(period)}`),

  /** The month's off-cycle accounts ('YYYY-MM') — active + assigned but NOT due this
   *  month by cadence, each with the next month it will bill (Admin). */
  listNotDue: (period: string): Promise<NotDue[]> =>
    api.get<NotDue[]>(`/agreement-fee-runs/not-due?period=${encodeURIComponent(period)}`),

  /** Restore a waived account-month to billable (Admin): removes the exclusion +
   *  recomputes that one account-month back into a run. 204. */
  restore: (exclusionId: UUID): Promise<void> =>
    api.post<void>(`/agreement-fee-runs/exclusions/${exclusionId}/restore`),

  /** Pre-download gating report for the accountant export (incomplete / drift). */
  exportReport: (period: string): Promise<FeeExportReport> =>
    api.get<FeeExportReport>(`/agreement-fee-runs/export-report?period=${encodeURIComponent(period)}`),

  /** Download the month's accountant .xlsx (streams a blob → browser save). */
  async downloadExport(period: string): Promise<void> {
    const res = await fetch(`/agreement-fee-runs/export?period=${encodeURIComponent(period)}`, {
      method: 'GET', credentials: 'include',
    });
    if (!res.ok) throw new Error(await _detailOf(res, 'The fee export could not be generated.'));
    const cd = res.headers.get('Content-Disposition') ?? '';
    downloadBlob(await res.blob(), _fileNameFromCD(cd, `fees_${period}.xlsx`));
  },
};
